import logo from '@busie/ui-kit/assets/logo-red-transparent.svg'
import { RouteResponse as Route } from '@busie/api'
import { GooglePlace, Location } from '@busie/utils'
import { FlexContainer, Loading, ErrorMessage } from '@busie/ui-kit'
import React from 'react'
import {
  BasicInfo,
  EnterLocation,
  ShowMore,
  Toolbar,
  PlaceInput,
} from '~/components'
import { PathfinderTrip } from '~/models'
import SidebarToggle from './SidebarToggle'
import SidebarStyled from './styled/SidebarStyled'

const routeStopToLocation = (stop: GooglePlace): Omit<Location, '_id'> => {
  return {
    id: stop.place_id || '',
    longitude: stop.geometry.location.lng(),
    latitude: stop.geometry.location.lat(),
    address: stop.formatted_address,
  }
}

type LocationOrNull = Omit<Location, '_id'> | null

interface Props {
  onLocationsChange: (pathfinderTrip: PathfinderTrip) => void
  onShowMoreClick: () => void
  route: Route | null
  isLoading: boolean
  errorMessage: string
}

const Sidebar: React.FC<React.PropsWithChildren<Props>> = ({
  onLocationsChange,
  onShowMoreClick,
  route,
  isLoading,
  errorMessage,
}) => {
  const [isOpen, setIsOpen] = React.useState(true)
  const [startLocation, setStartLocation] = React.useState<LocationOrNull>(null)
  const [endLocation, setEndLocation] = React.useState<LocationOrNull>(null)
  const [stopLocations, setStopLocations] = React.useState<LocationOrNull[]>([])

  React.useEffect(() => {
    if (startLocation && endLocation) {
      onLocationsChange({
        start: startLocation,
        stops: stopLocations.filter((sl) => sl !== null) as Location[],
        end: endLocation,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocation, endLocation, stopLocations])

  return (
    <SidebarStyled isOpen={isOpen}>
      <div style={{ display: isOpen ? 'inherit' : 'none' }}>
        <FlexContainer direction="column" w="100%">
          <FlexContainer
            justify="center"
            align="center"
            w="100%"
            sx={{ marginTop: '44px', marginBottom: '36px' }}
          >
            <img src={logo} style={{ height: '30px' }} alt="" />
          </FlexContainer>
          <FlexContainer direction="column" w="100%" rowGap={1}>
            <PlaceInput
              markerLabel="A"
              isStop={false}
              isEnd={false}
              inputPlaceholder="Search start point"
              onSelect={(place: GooglePlace) => {
                setStartLocation(routeStopToLocation(place))
              }}
            />
            {Array.isArray(stopLocations) &&
              stopLocations.map((location, index) => (
                <PlaceInput
                  key={index}
                  markerLabel={`${index + 1}`}
                  isStop
                  isEnd={false}
                  inputPlaceholder="Enter stop location"
                  onSelect={(place: GooglePlace) => {
                    setStopLocations(
                      stopLocations.map((stopLocation, stopIndex) => {
                        if (index === stopIndex) {
                          stopLocation = routeStopToLocation(place)
                        }
                        return stopLocation
                      })
                    )
                  }}
                />
              ))}
            <PlaceInput
              markerLabel="B"
              isStop={false}
              isEnd
              inputPlaceholder="Search destination"
              onSelect={(place: GooglePlace) => {
                setEndLocation(routeStopToLocation(place))
              }}
            />
          </FlexContainer>
          <FlexContainer fullWidth>
            <EnterLocation
              onClick={() => setStopLocations([...stopLocations, null])}
            />
          </FlexContainer>
          {!isLoading && errorMessage && (
            <FlexContainer fullWidth p={2}>
              <ErrorMessage style={{ fontSize: '14px' }}>
                {errorMessage}
              </ErrorMessage>
            </FlexContainer>
          )}
          {isLoading && (
            <FlexContainer justify="center" align="center" fullWidth>
              <Loading />
            </FlexContainer>
          )}
          {!isLoading && route && (
            <FlexContainer fullWidth pt={3} direction="column" rowGap={2}>
              <BasicInfo route={route} />
              <ShowMore onClick={onShowMoreClick} />
            </FlexContainer>
          )}
        </FlexContainer>

        <Toolbar hasRoute />
      </div>
      <SidebarToggle isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)} />
    </SidebarStyled>
  )
}

export default Sidebar
